import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Paper,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Alert } from "@mui/lab";

const AdminTermsAndConditions = () => {
  const [terms, setTerms] = useState([]); // Store terms
  const [newTerm, setNewTerm] = useState(""); // New term input
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [openDialog, setOpenDialog] = useState(false); // For edit dialog
  const [editTerm, setEditTerm] = useState(""); // Edit term input
  const [editTermId, setEditTermId] = useState(null); // Term ID for editing
  const [snackBarOpen, setSnackBarOpen] = useState(false); // Snackbar for success/error
  const [serviceType, setServiceType] = useState("payin"); // State for tab selection

  // Fetch terms based on serviceType (payin/payout)
  const fetchTC = async () => {
    try {
      const token = sessionStorage.getItem("jwtToken");
      const response = await axios.get(
        `https://api.ipaisa.co.in/myapp/auth/getTAndC`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            serviceType: serviceType, // Pass serviceType as query parameter
          },
        }
      );
      console.log(response.data?.body?.data);
      setTerms(response.data?.body?.data || []);
    } catch (error) {
      console.error("Error fetching terms:", error);
    }
  };

  // Fetch terms when serviceType changes
  useEffect(() => {
    fetchTC();
  }, [serviceType]);

  // Handle adding a new term
  const handleAddTerm = () => {
    if (newTerm.trim()) {
      axios
        .post(
          "https://api.ipaisa.co.in/myapp/admin/addTermsAndConditions",
          {
            termsAndConditions: newTerm,
          },
          {
            params: {
              serviceType: serviceType, // Pass serviceType while adding new term
            },
          }
        ) // POST new term
        .then((response) => {
          fetchTC();
          setNewTerm(""); // Clear input field
          setSnackBarOpen(true); // Show success snackbar
        })
        .catch((error) => {
          console.error("Error adding term:", error);
        });
    }
  };

  // Handle editing an existing term
  const handleEditTerm = () => {
    if (editTerm.trim()) {
      axios
        .put(
          `https://api.ipaisa.co.in/myapp/admin/updateTAndC`,
          { termsAndConditions: editTerm },
          { params: { id: editTermId } } // Pass termId as a query parameter
        ) // PUT updated term
        .then((response) => {
          fetchTC();
          setOpenDialog(false); // Close edit dialog
          setSnackBarOpen(true); // Show success snackbar
        })
        .catch((error) => {
          console.error("Error editing term:", error);
        });
    }
  };

  // Handle deleting a term
  const handleDeleteTerm = (id) => {
    axios
      .delete(`https://api.ipaisa.co.in/myapp/admin/deleteTAndC`, {
        params: { id: id },
      }) // DELETE term by ID
      .then(() => {
        fetchTC();
        setSnackBarOpen(true); // Show success snackbar
      })
      .catch((error) => {
        console.error("Error deleting term:", error);
      });
  };

  return (
    <Box sx={{ padding: "2rem", marginTop: "calc(3% + 56px)" }}>
      <Typography variant="h5">Manage Terms and Conditions</Typography>

      {/* Tabs for switching between Payin and Payout */}
      <Tabs
        value={serviceType}
        onChange={(event, newValue) => setServiceType(newValue)}
        aria-label="service type tabs"
      >
        <Tab label="Payin" value="payin" />
        <Tab label="Payout" value="payout" />
      </Tabs>

      {/* New Term Section */}
      <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
        <TextField
          label="New Term And Condition"
          value={newTerm}
          onChange={(e) => setNewTerm(e.target.value)}
          fullWidth
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddTerm}
          disabled={isLoading}
        >
          Add New
        </Button>
      </Box>

      {/* Terms List Section */}
      <Paper elevation={6} sx={{ borderRadius: 4 }}>
        <Box sx={{ mt: 4 }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <List>
              {terms.map((term, index) => (
                <ListItem key={term.id}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1">
                      🔹 {term.termsAndConditions}
                    </Typography>
                    <Divider />
                  </Box>
                  <IconButton
                    onClick={() => {
                      setEditTerm(term.termsAndConditions);
                      setEditTermId(term.id);
                      setOpenDialog(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteTerm(term.id)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      {/* Edit Term Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Edit Term</DialogTitle>
        <DialogContent>
          <Box sx={{ margin: 1 }}>
            <TextField
              label="Term"
              value={editTerm}
              onChange={(e) => setEditTerm(e.target.value)}
              fullWidth
              multiline
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleEditTerm} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Success/Error */}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackBarOpen(false)}
      >
        <Alert severity="success" onClose={() => setSnackBarOpen(false)}>
          Operation successful!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminTermsAndConditions;
